import { useNotificationContext } from "cerulean/src/NotificationContext";
import { useEffect, useState } from "react";
import { AoType, Product } from "../types";
import { getProducts } from "../api";

// this is lifted directly from support/react
export const PERSONAL_APP_TYPES = { personal: "personal" };
export const BUSINESS_APP_TYPES = {
  sole_proprietor: "sole_proprietor",
  general_partnership: "general_partnership",
  llc: "limited_liability_company",
  corporation: "corporation",
  nonprofit: "nonprofit",
};
function filterProductsByType(products: Product[], isBusiness: boolean) {
  const filteredProducts = products?.filter((product) => {
    const foundType = product.exclusive_application_types?.some((r) =>
      isBusiness
        ? Object.values(BUSINESS_APP_TYPES).includes(r)
        : Object.values(PERSONAL_APP_TYPES).includes(r)
    );
    if (
      foundType ||
      product.exclusive_application_types === undefined ||
      product.exclusive_application_types?.length === 0
    ) {
      return true;
    }
    return false;
  });
  return filteredProducts;
}

const useProducts = () => {
  const { sendNegativeNotification } = useNotificationContext();

  const [products, setProducts] = useState<any[]>([]);
  const [uatProducts] = useState<any[]>([]);
  const [allowDebitCardRequiredByProduct, setAllowDebitCardRequiredByProduct] =
    useState<boolean>(false);

  const fetchProducts = async () => {
    // Fetch all products (both online and staff-led) in one API call
    const response = await getProducts();

    if (!response.ok) {
      sendNegativeNotification("There was an error fetching products.");
      return;
    }

    const data = await response.json();

    setProducts(data.results);
    setAllowDebitCardRequiredByProduct(data.allow_debit_card_required_by_product || false);
  };

  const updateProduct = (product: Product) => {
    setProducts((prev) => {
      const index = prev.findIndex((p) => p.uuid === product.uuid);
      if (index === -1) return prev;
      return [...prev.slice(0, index), product, ...prev.slice(index + 1)];
    });
  };

  const deleteProduct = (product: Product) => {
    setProducts((prev) => prev.filter((p) => p.uuid !== product.uuid));
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  // Single unified function to get products by type and flow category
  const getProductsForType = (type: AoType, aoFlowCategory: "online" | "staff_led" = "online") => {
    const isBusiness = type === "business" || type.includes("business");
    const filteredByFlow = products.filter((p) => p.ao_flow_category === aoFlowCategory);
    const filteredByType = filterProductsByType(filteredByFlow, isBusiness);
    return filteredByType.sort((a, b) => a.display_order - b.display_order);
  };

  // Backwards compatibility wrappers
  const productsForType = (type: AoType) => getProductsForType(type, "online");
  const staffLedProductsForType = (type: AoType) => getProductsForType(type, "staff_led");

  return {
    uatProducts,
    products,
    getProductsForType,
    productsForType,
    staffLedProductsForType,
    updateProduct,
    deleteProduct,
    allowDebitCardRequiredByProduct,
  };
};

export default useProducts;
